import { Badge } from "design-system/atoms/Badge"
import { cn } from "design-system/utils"
import { Check, CircleDashed, CirclePause, PlayCircle, Rocket } from "lucide-react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { CampaignStatus, CampaignStatusEnum } from "schema"

// Utility function to get status text and icon
const getCampaignStatusInfo = (status: CampaignStatus, t: (key: string) => string, size = 12) => {
  const statusText = {
    Draft: t("campaigns.statuses.draft"),
    Active: t("campaigns.statuses.active"),
    Paused: t("campaigns.statuses.paused"),
    Launched: t("campaigns.statuses.launched"),
    Finished: t("campaigns.statuses.finished"),
  }

  const statusIcon = {
    Draft: <CircleDashed size={size} />,
    Active: <PlayCircle className="text-chart-1" size={size} />,
    Paused: <CirclePause size={size} />,
    Launched: <Rocket className="text-chart-1" size={size} />,
    Finished: <Check className="text-success dark:text-success" size={size} />,
  }

  return {
    text: statusText[status],
    icon: statusIcon[status],
  }
}

type CampaignStatusBadgeProps = {
  status: CampaignStatus
  className?: string
  size?: "sm" | "md" | "lg"
}

export const CampaignStatusBadge = ({
  status,
  className,
  size = "md",
}: CampaignStatusBadgeProps) => {
  const { t } = useTranslation()

  const { text: statusText, icon: statusIcon } = useMemo(() => {
    const statusSize = {
      sm: 12,
      md: 16,
      lg: 18,
    }

    return getCampaignStatusInfo(status, t, statusSize[size])
  }, [status, size, t])

  return (
    <Badge
      variant="outline"
      className={cn(
        "text-muted-foreground",
        size === "md" && "px-2 py-1",
        size === "lg" && "px-4 py-2",
        className,
      )}
    >
      <div
        className={cn(
          "flex flex-row items-center gap-2",
          [CampaignStatusEnum.Launched, CampaignStatusEnum.Active].includes(status) &&
            "text-gradient-primary",
        )}
      >
        {statusIcon}
        <span>{statusText}</span>
      </div>
    </Badge>
  )
}
